import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@drone-js/core';
import { changeStyleInElement, findElementHeight, findTagByDataElementName } from 'core/utils/domUtils';
import { selectIsAnyOverlayVisible } from 'store/slice/overlay';
import ContentArea from 'component/common/ContentArea/ContentArea';

interface StickyTopBarProps {
    testId?: string;
    elementName?: string;
    sticky?: boolean;
    contentAreaProps?: React.ComponentProps<typeof ContentArea>;
    elementNameToCalcHeight?: string;
    targetAttribute?: 'height' | 'top' | 'width' | 'bottom';
    fullWidth?: boolean;
    zIndex?: number;
    noShadow?: boolean;
    noBorder?: boolean;
    stickyTop?: number;
}

export const StickyTopBar: React.FC<React.PropsWithChildren<StickyTopBarProps>> = (props) => {
    const isAnyOverlayVisible: boolean = useSelector(selectIsAnyOverlayVisible);
    const stickyBarRef = React.createRef<HTMLDivElement>();
    const { contentAreaProps, elementNameToCalcHeight, targetAttribute } = props;
    const sticky = props.sticky ?? true;

    React.useEffect(() => {
        if (!stickyBarRef.current || !elementNameToCalcHeight || !targetAttribute) {
            return;
        }
        const element = findTagByDataElementName(elementNameToCalcHeight);
        if (element) {
            changeStyleInElement(stickyBarRef.current, { [targetAttribute]: `${findElementHeight(element)}px` });
        }
    }, [elementNameToCalcHeight, targetAttribute, stickyBarRef]);

    return (
        <Box
            data-testid={props.elementName}
            data-element-name={props.elementName}
            display="flex"
            justifyContent="center"
            borderStyle="solid"
            borderRadius="0"
            borderColor="base.default"
            backgroundColor="base.surface"
            boxShadow={props.noShadow ? 'none' : 'bottom-s'}
            borderWidth={[props.noBorder ? '0' : '1', '0', '0', '0']}
            aria-hidden={isAnyOverlayVisible}
            ref={stickyBarRef}
            position={sticky ? 'sticky' : undefined}
            zIndex={props.zIndex ?? 3}
            top={sticky ? `${props.stickyTop ?? 0}px` : undefined}
        >
            {props.fullWidth ? (
                props.children
            ) : (
                <ContentArea mx="auto" width="100%" flex={1} {...contentAreaProps}>
                    {props.children}
                </ContentArea>
            )}
        </Box>
    );
};
StickyTopBar.displayName = 'StickyTopBar';
